import React, { useEffect, useRef, useState } from 'react';
import { createMenuProduct, updateMenuProduct } from '../services/menuProductService';
import { resolveImageUrl } from '../utils/media';

const EMPTY_FORM = { name: '', price: '' };

export default function MenuProductForm({ menuProduct, onCancel, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const objectUrlRef = useRef('');

  const isEditing = Boolean(menuProduct);

  useEffect(() => {
    if (menuProduct) {
      setForm({
        name: menuProduct.name || '',
        price: menuProduct.price != null ? String(menuProduct.price) : '',
      });
      setImagePreview(resolveImageUrl(menuProduct.image));
    } else {
      setForm(EMPTY_FORM);
      setImagePreview('');
    }
    setImageFile(null);
    setError('');
  }, [menuProduct]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  function handleChange(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const previewUrl = URL.createObjectURL(file);
    objectUrlRef.current = previewUrl;

    setImageFile(file);
    setImagePreview(previewUrl);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    const formData = new FormData();
    formData.append('name', form.name.trim());
    formData.append('price', Number(form.price) || 0);
    if (imageFile) formData.append('image', imageFile);

    try {
      if (isEditing) {
        await updateMenuProduct(menuProduct.id, formData);
      } else {
        await createMenuProduct(formData);
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.msg || 'No se pudo guardar el producto del menú.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="section-card"
        style={{ width: '100%', maxWidth: 560, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
      >
      <div className="section-header">
        <h6>
          <i className={`bi ${isEditing ? 'bi-pencil-square' : 'bi-plus-circle'}`} style={{ color: 'var(--orig-green)' }} />
          {isEditing ? 'Editar Producto del Menú' : 'Nuevo Producto del Menú'}
        </h6>
        <button
          type="button"
          onClick={onCancel}
          style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
          title="Cerrar"
        >
          ×
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="section-body">
          {error && (
            <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
              <i className="bi bi-exclamation-triangle-fill" />
              {error}
            </div>
          )}

          <div className="row g-3">
            <div className="col-12 col-md-8">
              <label className="form-label">Nombre</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: Hamburguesa Clásica"
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                required
              />
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">Precio</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                placeholder="0.00"
                value={form.price}
                onChange={e => handleChange('price', e.target.value)}
                required
              />
            </div>

            <div className="col-12">
              <label className="form-label">
                Imagen <span style={{ opacity: 0.5, fontSize: '0.8em' }}>(opcional)</span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 72, height: 72, borderRadius: 'var(--orig-radius)',
                  border: '1px solid var(--orig-border)', background: 'var(--orig-panel)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden', flexShrink: 0,
                }}>
                  {imagePreview ? (
                    <img src={imagePreview} alt="Vista previa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <i className="bi bi-image" style={{ fontSize: 24, color: 'var(--orig-text-hint)' }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    className="form-control"
                    onChange={handleImageChange}
                  />
                  <div style={{ fontSize: 11.5, color: 'var(--orig-text-muted)', marginTop: 4 }}>
                    JPG, PNG, WEBP o GIF. Máximo 5MB.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
        }}>
          <button type="button" className="btn-orig btn-orig-outline" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="btn-orig btn-orig-green" disabled={saving}>
            <i className="bi bi-check-lg" />
            {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear Producto'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
